/**
 * 共用登入頁。所有 app 使用同一份，只以 props 換掉標題與說明。
 *
 * **為什麼沒有帳號密碼欄位。**本系統採 BFF 架構：密碼只在 Keycloak 的登入頁
 * 輸入，前端全程碰不到帳密，也拿不到 access token。要在這裡做表單，就得讓前端把
 * 帳密送去後端換 token（OAuth 的 ROPC 流程）—— 那會繞過 Keycloak 的 MFA 與帳號
 * 鎖定，在 OAuth 2.1 已被移除，auth-service 也刻意關閉了該流程。
 *
 * 所以這一頁的職責是「說明這是什麼、把人送去 Keycloak」，按鈕是整頁導向而非
 * fetch —— OIDC 的 Authorization Code 流程需要瀏覽器實際走一趟。
 */

import type { ReactNode } from 'react';
import { Button } from '@shared/ui';
import { cn } from '@shared/utils';
import { googleLoginUrl, loginUrl, registerUrl } from '@shared/auth';

export interface LoginScreenProps {
  /** app 名稱，顯示為主標題 */
  appName: string;
  /** 一句話說明這個 app 是做什麼的 */
  description?: string;
  /** app 專屬的圖示，置於標題上方；未提供時留白 */
  icon?: ReactNode;
  /**
   * 登入狀態查詢失敗時的訊息（與「未登入」不同，需讓使用者知道問題出在哪）。
   *
   * 刻意沒有搭配的「重試」按鈕：查詢失敗時使用者分兩種——本來就沒登入的按了重試
   * 只會拿到 401 再回到這一頁，本來有登入的則按下方任一顆登入按鈕就會因為 Keycloak
   * 的 SSO session 仍在而直接彈回 app，不必重打密碼。兩種情況既有的按鈕都已涵蓋。
   */
  errorMessage?: string;
  className?: string;
}

export function LoginScreen({
  appName,
  description,
  icon,
  errorMessage,
  className,
}: LoginScreenProps) {
  return (
    <main
      className={cn(
        'relative flex min-h-dvh flex-col items-center justify-center px-6 py-12',
        className,
      )}
    >
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          {icon ? (
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-border bg-card text-accent">
              {icon}
            </div>
          ) : null}

          <h1 className="font-display text-3xl tracking-tight text-foreground">
            {appName}
          </h1>

          {description ? (
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>

        {errorMessage ? (
          <div
            role="alert"
            className="mt-8 rounded-lg border border-error/30 bg-error-muted px-4 py-3 text-sm text-error"
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-10 flex flex-col gap-3">
          {/* 三顆都是 asChild + <a>：這是整頁導向，語義上是連結而非按鈕，
              也讓「在新分頁開啟」等瀏覽器行為維持正常。

              Google 只有一顆、不分登入與註冊：首次登入時 Keycloak 會自動建立帳號，
              兩者本來就是同一條路徑，做成兩顆只會讓人以為選錯了會出事。 */}
          <Button
            asChild
            variant="secondary"
            size="lg"
            fullWidth
            className="gap-3"
          >
            <a href={googleLoginUrl()}>
              <GoogleMark />
              使用 Google 繼續
            </a>
          </Button>

          <div className="flex items-center gap-3 py-1" aria-hidden="true">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">或</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Button asChild size="lg" fullWidth>
            <a href={loginUrl()}>以密碼登入</a>
          </Button>

          <Button asChild variant="ghost" size="lg" fullWidth>
            <a href={registerUrl()}>建立帳號</a>
          </Button>
        </div>
      </div>
    </main>
  );
}

/**
 * Google 的 G 標記。
 *
 * **這裡的色值字面量是「顏色一律用設計 token」的刻意例外。**這四色是 Google 的
 * 商標，不是本站的設計語彙 —— 把它們登記進 `@theme` 會讓一個第三方品牌混進自家色票，
 * 日後有人拿 `--color-google-blue` 去畫別的東西也不會有人攔；而改用既有 token 或
 * `currentColor` 則等於竄改商標。兩條路都比留在這裡當字面量差。
 *
 * 同理不放進 `@shared/ui` 的 icon 集：那裡是可換色、可換尺寸的介面線稿，
 * 商標的規則相反，混在一起遲早會有人「順手」把它改成單色。
 *
 * 彩色 G 直接置於深色底是 Google 品牌規範允許的組合，不需要另外墊白底。
 */
function GoogleMark() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden="true"
      focusable="false"
      className="shrink-0"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
